import React, { useState, useEffect } from 'react';
import { Timer, Play, Search, Save } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { toast } from 'sonner';
import { retentionAPI } from '../../services/api';

const FIELDS = [
  { key: 'contacts_days', label: 'Messaggi di contatto', hint: 'Giorni dalla ricezione' },
  { key: 'dpo_contacts_days', label: 'Richieste al DPO', hint: 'Giorni dalla ricezione' },
  { key: 'job_applications_days', label: 'Candidature (CV incluso)', hint: 'Giorni dall\'invio' },
  { key: 'whistleblowing_days', label: 'Segnalazioni chiuse', hint: 'Giorni dalla chiusura (max 5 anni di legge)' },
  { key: 'form_submissions_days', label: 'Log anti-bot', hint: 'Giorni di conservazione' },
];

export const RetentionSettingsCard = () => {
  const [settings, setSettings] = useState(null);
  const [saving, setSaving] = useState(false);
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(null);

  const load = async () => {
    try {
      const res = await retentionAPI.getSettings();
      setSettings(res.data);
    } catch (e) {
      toast.error('Errore nel caricamento dei termini di conservazione');
    }
  };

  useEffect(() => { load(); }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = { enabled: settings.enabled };
      FIELDS.forEach((f) => { payload[f.key] = Number(settings[f.key]); });
      const res = await retentionAPI.updateSettings(payload);
      setSettings(res.data);
      toast.success('Termini di conservazione salvati');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Errore nel salvataggio');
    } finally {
      setSaving(false);
    }
  };

  const handlePreview = async () => {
    setBusy(true);
    try {
      const res = await retentionAPI.preview();
      setPreview(res.data);
    } catch (error) {
      toast.error('Errore nel calcolo');
    } finally {
      setBusy(false);
    }
  };

  const handleCleanup = async () => {
    if (!window.confirm('Eseguire ora la cancellazione dei dati oltre i termini? L\'operazione non è reversibile.')) return;
    setBusy(true);
    try {
      const res = await retentionAPI.cleanup();
      setPreview(res.data);
      toast.success('Pulizia completata');
      load();
    } catch (error) {
      toast.error('Errore durante la pulizia');
    } finally {
      setBusy(false);
    }
  };

  if (!settings) return null;

  return (
    <Card className="glass-card" data-testid="retention-card">
      <CardHeader>
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
            <Timer className="w-5 h-5 text-primary" />
          </div>
          <div>
            <CardTitle className="text-2xl">Conservazione dei dati</CardTitle>
            <CardDescription>
              Termini oltre i quali i dati personali vengono cancellati (le segnalazioni chiuse vengono anonimizzate
              mantenendo solo un record statistico). Con la pulizia automatica attiva il controllo gira una volta al giorno.
            </CardDescription>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-6">
        <label className="flex items-center gap-3 text-sm cursor-pointer">
          <input
            type="checkbox"
            checked={!!settings.enabled}
            onChange={(e) => setSettings({ ...settings, enabled: e.target.checked })}
            className="h-4 w-4 accent-primary focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-ring"
            data-testid="retention-enabled-checkbox"
          />
          Pulizia automatica giornaliera attiva
        </label>

        <div className="grid md:grid-cols-2 gap-4">
          {FIELDS.map((f) => (
            <div key={f.key}>
              <Label htmlFor={f.key}>{f.label}</Label>
              <Input
                id={f.key}
                type="number"
                min={1}
                value={settings[f.key] ?? ''}
                onChange={(e) => setSettings({ ...settings, [f.key]: e.target.value })}
                className="mt-2"
                data-testid={`retention-${f.key}`}
              />
              <p className="text-xs text-muted-foreground mt-1">{f.hint}</p>
            </div>
          ))}
        </div>

        <div className="flex flex-wrap gap-3">
          <Button onClick={handleSave} disabled={saving} data-testid="retention-save-btn">
            <Save className="w-4 h-4 mr-2" />
            {saving ? 'Salvataggio...' : 'Salva termini'}
          </Button>
          <Button variant="outline" onClick={handlePreview} disabled={busy} data-testid="retention-preview-btn">
            <Search className="w-4 h-4 mr-2" />
            Verifica cosa verrebbe cancellato
          </Button>
          <Button variant="destructive" onClick={handleCleanup} disabled={busy} data-testid="retention-cleanup-btn">
            <Play className="w-4 h-4 mr-2" />
            Esegui pulizia ora
          </Button>
        </div>

        {preview && (
          <div className="p-4 rounded-lg bg-muted/40 text-sm space-y-1" data-testid="retention-result">
            <p className="font-medium">
              {preview.dry_run ? 'Simulazione (nessun dato cancellato)' : 'Pulizia eseguita'}
            </p>
            <p>Messaggi di contatto: {preview.contacts}</p>
            <p>Richieste DPO: {preview.dpo_contacts}</p>
            <p>Candidature: {preview.job_applications}</p>
            <p>Segnalazioni chiuse anonimizzate: {preview.whistleblowing_reports}</p>
            <p>Log anti-bot: {preview.form_submissions}</p>
            <p>OTP scaduti: {preview.expired_otps}</p>
          </div>
        )}

        {settings.last_run_at && (
          <p className="text-xs text-muted-foreground" data-testid="retention-last-run">
            Ultima pulizia: {new Date(settings.last_run_at).toLocaleString('it-IT')}
          </p>
        )}
      </CardContent>
    </Card>
  );
};

export default RetentionSettingsCard;
